/**
 * The rulebook as it stands after the committee — bundled corpus plus what was committed over it.
 *
 * `RULES` is generated data shipped with the build, so it cannot know what the room decided last
 * night. `effectiveRules` folds the committed outcomes in; this is the subscription that keeps
 * that answer live, so a rule made permanent at 9:41pm reads as permanent on every screen at
 * 9:41pm rather than at the next deploy.
 *
 * ONE HOOK, so the Rules pane and the Proposals pane cannot disagree about what is in force —
 * which is the pair that disagreed before it existed: the docket said an item had passed and the
 * rulebook two tabs away still called it provisional.
 */

import { useEffect, useState } from 'react';
import { RULES, type RulebookEntry } from '../domain/rulebook.js';
import { decisionsFrom, effectiveRules, type Decision } from '../domain/ruleDecisions.js';
import type { LeagueStore } from '../store/types.js';

export interface Rulebook {
  rules: readonly RulebookEntry[];
  decisions: readonly Decision[];
}

export function useRulebook(store: LeagueStore, season: number): Rulebook {
  const [book, setBook] = useState<Rulebook>({ rules: RULES, decisions: [] });

  useEffect(() => {
    let live = true;
    const load = () => Promise.all([store.proposals(season), store.docket(season)])
      .then(([proposals, docket]) => {
        if (!live) return;
        const decisions = decisionsFrom({
          proposals,
          committedAt: docket?.committedAt,
          order: docket?.order,
        });
        setBook({ rules: effectiveRules(RULES, decisions), decisions });
      });
    void load();
    const off = store.subscribe(season, (what) => {
      if (what === 'proposals' || what === 'docket') void load();
    });
    return () => { live = false; off(); };
  }, [store, season]);

  return book;
}
